import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  ActionKey,
  type MemberMembershipFeeConfirm,
  type MemberMembershipFeeState,
  type MemberChargeReceipt,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore, sessionVenueKey } from '../../store/auth.store';

/** A joining source is required; unknown historical membership never becomes a fee. */
export function MemberMembershipFee({ memberId }: { memberId: string }) {
  const scope = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
      memberId,
    ]),
  );
  return <MembershipFeeSession key={scope} memberId={memberId} identity={scope} />;
}
function MembershipFeeSession({ memberId, identity }: { memberId: string; identity: string }) {
  const { t } = useTranslation('tournament');
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current = () =>
    mounted.current &&
    identity ===
      JSON.stringify([
        useAuthStore.getState().user?.id,
        sessionVenueKey(useAuthStore.getState().accessToken),
        useAuthStore.getState().sessionRevision,
        useAuthStore.getState().allowedActions,
        memberId,
      ]);
  const user = useAuthStore((s) => s.user);
  const actions = useAuthStore((s) => s.allowedActions);
  const can = (key: string) => !!actions?.includes(key);
  const visiblePages = useAuthStore((s) => s.visiblePages);
  const canUpgrade = can(ActionKey.ACCOUNTING_OPEN) && visiblePages?.includes('/admin/venues');
  const venueId = sessionVenueKey(useAuthStore.getState().accessToken);
  const qc = useQueryClient();
  const recoveryKey = `membership-fee-request:v2:${sessionVenueKey(useAuthStore.getState().accessToken)}:${user?.id}:${memberId}`;
  const [pending, setPending] = useState<MemberMembershipFeeConfirm | null>(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(recoveryKey) ?? 'null');
      return saved && typeof saved.joiningId === 'string' && typeof saved.operationKey === 'string'
        ? saved
        : null;
    } catch {
      return null;
    }
  });
  const [legacyRecord] = useState<MemberMembershipFeeConfirm | null>(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem(`membership-fee-request:${user?.id}:${memberId}`) ?? 'null',
      );
    } catch {
      return null;
    }
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [receipt, setReceipt] = useState<MemberChargeReceipt | null>(null);
  const state = useQuery({
    queryKey: ['member-membership-fee', memberId, identity],
    queryFn: () => api.get<MemberMembershipFeeState | null>(`/members/${memberId}/membership-fee`),
    enabled: can(ActionKey.MEMBER_CHARGE_READ) || can(ActionKey.MEMBER_MEMBERSHIP_FEE_CONFIRM),
    retry: false,
  });
  const noFee = state.data && 'noFeeReason' in state.data ? state.data : null;
  const joining = state.data && !('noFeeReason' in state.data) ? state.data : null;
  const upgradeAccess = useQuery({
    queryKey: ['membership-upgrade-access', identity],
    queryFn: () => api.get('/accounting/cash-contract-upgrade'),
    enabled:
      !!canUpgrade && !joining?.paidAt && joining?.collectionState === 'CASH_BAG_UPGRADE_REQUIRED',
    retry: false,
  });
  const legacyUnknown = legacyRecord?.joiningId === joining?.id && !!joining && !joining.paidAt;
  // Older records lack venue scope. Adopt only after the server proves the exact
  // immutable joining belongs to this actor's current venue; never bind by member alone.
  useEffect(() => {
    if (pending || !joining?.id || !joining.paidAt || !current()) return;
    try {
      const legacy = JSON.parse(
        sessionStorage.getItem(`membership-fee-request:${user?.id}:${memberId}`) ?? 'null',
      );
      if (
        legacy?.joiningId === joining.id &&
        typeof legacy.operationKey === 'string' &&
        /^[0-9a-f-]{36}$/i.test(legacy.operationKey)
      ) {
        sessionStorage.setItem(recoveryKey, JSON.stringify(legacy));
        setPending(legacy);
      }
    } catch {
      /* Retain the original bytes for explicit recovery. */
    }
  }, [joining?.id, joining?.paidAt]);
  async function confirm(body: MemberMembershipFeeConfirm) {
    if (!pending && (joining?.collectionState !== 'READY' || legacyUnknown)) return;
    if (
      busy ||
      !current() ||
      !can(ActionKey.MEMBER_MEMBERSHIP_FEE_CONFIRM) ||
      (joining && body.joiningId !== joining.id)
    )
      return;
    try {
      sessionStorage.setItem(recoveryKey, JSON.stringify(body));
    } catch {
      setMessage('recoveryUnavailable');
      return;
    }
    setPending(body);
    setBusy(true);
    setMessage('');
    try {
      const result = await api.post<MemberChargeReceipt>(
        `/members/${memberId}/membership-fee/confirm`,
        body,
      );
      sessionStorage.removeItem(recoveryKey);
      const legacyKey = `membership-fee-request:${user?.id}:${memberId}`;
      try {
        const legacy = JSON.parse(sessionStorage.getItem(legacyKey) ?? 'null');
        if (legacy?.joiningId === body.joiningId && legacy.operationKey === body.operationKey)
          sessionStorage.removeItem(legacyKey);
      } catch {
        /* Unrelated or malformed recovery evidence stays intact. */
      }
      setPending(null);
      if (!current()) return;
      setReceipt(result);
      void qc.invalidateQueries({ queryKey: ['member-membership-fee', memberId, identity] });
    } catch (error) {
      if (!current()) return;
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        sessionStorage.removeItem(recoveryKey);
        setPending(null);
        setMessage('rejected');
        void qc.invalidateQueries({ queryKey: ['member-membership-fee', memberId, identity] });
      } else setMessage('uncertain');
    } finally {
      if (current()) setBusy(false);
    }
  }
  if (!can(ActionKey.MEMBER_CHARGE_READ) && !can(ActionKey.MEMBER_MEMBERSHIP_FEE_CONFIRM))
    return null;
  return (
    <section
      className="border border-base-300 rounded-xl p-3 my-3 space-y-2"
      aria-label={t('cashier.membershipFee')}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">{t('cashier.membershipFee')}</h2>
        <button
          type="button"
          className="min-h-11 px-3 rounded border border-base-300"
          disabled={state.isFetching}
          onClick={() => void state.refetch()}
        >
          {t('cashier.refreshMembership')}
        </button>
      </div>
      {state.isPending && <p>{t('cashier.loading')}</p>}
      {state.isError && <p role="alert">{t('cashier.loadError')}</p>}
      {state.isSuccess && !state.data && <p>{t('cashier.membershipUnknown')}</p>}
      {state.isSuccess && noFee && <p>{t(`cashier.membershipNoFee.${noFee.noFeeReason}`)}</p>}
      {joining && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">
              {joining.amountDue} · {t('cashier.CASH')}
            </span>
            <span>
              {joining.paidAt
                ? t('cashier.saved')
                : joining.collectionState === 'READY'
                  ? t('cashier.membershipReady')
                  : t(`cashier.membershipBlocked.${joining.collectionState ?? 'UNKNOWN'}`, {
                      defaultValue: t('cashier.membershipBlocked.UNKNOWN'),
                    })}
            </span>
          </div>
          <details>
            <summary className="min-h-11 flex items-center cursor-pointer underline">
              {t('cashier.membershipDetails')}
            </summary>
            <div className="space-y-2 py-2">
              <p>{t('cashier.membershipNote')}</p>
              {legacyUnknown && (
                <p role="alert">
                  {t('cashier.membershipLegacyPending', { key: legacyRecord?.operationKey })}
                </p>
              )}
              {!joining.paidAt && joining.collectionState !== 'READY' && (
                <p role="status">
                  {t(`cashier.membershipBlocked.${joining.collectionState ?? 'UNKNOWN'}`, {
                    defaultValue: t('cashier.membershipBlocked.UNKNOWN'),
                  })}
                </p>
              )}
              {!joining.paidAt && joining.collectionState === 'CASH_BAG_UPGRADE_REQUIRED' && (
                <div className="space-y-2">
                  <p>{t('cashier.membershipUpgrade.prerequisite')}</p>
                  {canUpgrade &&
                  upgradeAccess.isSuccess &&
                  !upgradeAccess.isFetching &&
                  !upgradeAccess.isError ? (
                    <Link
                      className="inline-flex min-h-11 items-center underline"
                      to={`/admin/venues?onboarding=${encodeURIComponent(venueId ?? '')}`}
                    >
                      {t('cashier.membershipUpgrade.link')}
                    </Link>
                  ) : (
                    <p>{t('cashier.membershipUpgrade.askAdmin')}</p>
                  )}
                </div>
              )}
              {!joining.paidAt && joining.collectionState === 'READY' && (
                <p>{t(`cashier.membershipDestination.${joining.cashDestination}`)}</p>
              )}
              <p>
                {t('cashier.total')}: {joining.amountDue} · {t('cashier.CASH')}
              </p>
              {joining.paidAt && (
                <p>
                  {t('cashier.saved')} · {joining.receiptId} ·{' '}
                  {new Date(joining.paidAt).toLocaleString()}
                </p>
              )}
            </div>
          </details>
          {!joining.paidAt && !pending && can(ActionKey.MEMBER_MEMBERSHIP_FEE_CONFIRM) && (
            <button
              type="button"
              className="rounded bg-primary text-primary-content p-2"
              disabled={busy || joining.collectionState !== 'READY' || legacyUnknown}
              onClick={() =>
                void confirm({
                  joiningId: joining!.id,
                  operationKey: crypto.randomUUID(),
                  sessionId: joining!.sessionId ?? undefined,
                  cashBagContractVersion: joining!.cashBagContractVersion ?? undefined,
                })
              }
            >
              {t('cashier.confirmMembershipFee')}
            </button>
          )}
        </>
      )}
      {pending && can(ActionKey.MEMBER_MEMBERSHIP_FEE_CONFIRM) && (
        <button type="button" disabled={busy} onClick={() => void confirm(pending)}>
          {t('cashier.retry')}
        </button>
      )}
      {message && <p role="alert">{t(`cashier.${message}`)}</p>}
      {receipt && (
        <p role="status">
          {t('cashier.saved')} {receipt.id} · {receipt.netDue} · {receipt.tender}
        </p>
      )}
    </section>
  );
}
